import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Box, Flex, Pressable, Text, xcss } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import Popup from '@atlaskit/popup';
import {
  ProductIcon,
  getProductDisplayName,
  sortProducts,
} from '@app/app-updates-product-app';
import type { UiProductType } from '@app/app-updates-product-app';

const messages = defineMessages({
  noProducts: {
    id: 'appUpdates.productList.noProducts',
    defaultMessage: 'No apps',
    description: 'Text displayed when no apps are associated with the update',
  },
  additionalProducts: {
    id: 'appUpdates.productList.additionalProducts',
    defaultMessage: '+{count}',
    description: 'Text for badge showing additional apps count',
  },
  additionalProductsAriaLabel: {
    id: 'appUpdates.productList.additionalProducts.ariaLabel',
    defaultMessage: 'Show {count} more products',
    description: 'Aria label for the additional products badge button',
  },
  additionalProductsTooltip: {
    id: 'appUpdates.productList.additionalProductsTooltip',
    defaultMessage: 'Additional apps',
    description: 'Tooltip title for additional apps',
  },
});

const styles = {
  container: xcss({
    display: 'flex',
    alignItems: 'center',
    gap: 'space.100',
  }),
  additionalProductsBadge: xcss({
    marginLeft: 'space.075',
  }),
  popupProduct: xcss({
    display: 'flex',
    alignItems: 'center',
    gap: 'space.075',
    padding: 'space.025',
  }),
  popupContainer: xcss({
    display: 'flex',
    flexDirection: 'column',
    gap: 'space.050',
    padding: 'space.100',
  }),
  popupTrigger: xcss({
    backgroundColor: 'color.background.neutral.subtle',
    padding: 'space.0',
  }),
};

export interface ProductListProps {
  productTypes?: UiProductType[] | null;
}

export const ProductList: React.FC<ProductListProps> = ({ productTypes }) => {
  const { formatMessage } = useIntl();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  if (!productTypes || productTypes.length === 0) {
    return (
      <Text color="color.text.subtlest" size="small">
        {formatMessage(messages.noProducts)}
      </Text>
    );
  }

  // Sort products by priority so key products (e.g. Jira) are shown first
  const sortedProductTypes = sortProducts(productTypes);

  if (sortedProductTypes.length === 1) {
    return (
      <Flex xcss={styles.container} testId="product-list-single">
        <ProductIcon
          productType={sortedProductTypes[0]}
          size="small"
          testId="product-list-icon"
        />
        <Text weight="medium" color="color.text">
          {getProductDisplayName(sortedProductTypes[0])}
        </Text>
      </Flex>
    );
  }

  // For multiple products, show first product with icon + count badge with popup
  const [firstProductType, ...remainingProductTypes] = sortedProductTypes;

  const openPopup = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    closeTimeoutRef.current = setTimeout(() => setIsPopupOpen(false), 150);
  };

  const getPopupContent = () => (
    <Box
      xcss={styles.popupContainer}
      testId="additional-products-popup"
      onMouseEnter={openPopup}
      onMouseLeave={closePopup}
    >
      {remainingProductTypes.map((productType, index) => (
        <Box key={`${productType}-${index}`} xcss={styles.popupProduct}>
          <ProductIcon
            productType={productType}
            size="small"
            testId={`popup-product-icon-${index}`}
          />
          <Text color="color.text">{getProductDisplayName(productType)}</Text>
        </Box>
      ))}
    </Box>
  );

  return (
    <Flex xcss={styles.container} testId="product-list-multiple">
      <ProductIcon
        productType={firstProductType}
        size="small"
        testId="product-list-primary-icon"
      />
      <Text weight="medium" color="color.text">
        {getProductDisplayName(firstProductType)}
      </Text>
      <Box xcss={styles.additionalProductsBadge}>
        <Popup
          isOpen={isPopupOpen}
          onClose={closePopup}
          placement="bottom-start"
          content={getPopupContent}
          testId="additional-products-popup-trigger"
          trigger={(triggerProps) => (
            <Pressable
              {...triggerProps}
              onMouseEnter={openPopup}
              onMouseLeave={closePopup}
              testId="additional-products-badge-wrapper"
              xcss={styles.popupTrigger}
              aria-label={formatMessage(messages.additionalProductsAriaLabel, {
                count: remainingProductTypes.length,
              })}
            >
              <Badge appearance="default" testId="additional-products-badge">
                {formatMessage(messages.additionalProducts, {
                  count: remainingProductTypes.length,
                })}
              </Badge>
            </Pressable>
          )}
        />
      </Box>
    </Flex>
  );
};
